export interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
}

export interface Musician {
  id: string;
  first_name: string;
  last_name: string;
  photo_url?: string;
  photo_storage_path?: string;
  has_photo?: boolean;
  bio?: string;
  genres?: string[];
  soundcloud_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  website_url?: string;
  instruments?: string[];
  user_id?: string;
  claimed?: boolean;
  claimed_at?: string;
}

export interface MusicianLink {
  id: string;
  musician_id: string;
  title: string;
  url: string;
  order: number;
  is_deleted?: boolean;
  created_at?: string;
  created_by_id?: string;
  updated_at?: string;
}

export interface Band {
  id: string;
  name: string;
  genre?: string;
  genres?: string[];
  ownerId?: string;
  created_by_id?: string;
  created_by_email?: string;
  description?: string;
  bio?: string;
  photo_url?: string;
  photo_storage_path?: string;
  soundcloud_url?: string;
  spotify_url?: string;
  instagram_url?: string;
  city?: string;
  state?: string;
  created_at?: string;
  updated_at?: string;
  claimed?: boolean;
  claimed_at?: string;
  claimed_by_user_id?: string;
  claimed_by_user_email?: string;
}

export interface Venue {
  id: string;
  name: string;
  address: string;
  capacity?: number;
  latitude?: number;
  longitude?: number;
  photo_url?: string;
}

export interface BandMember {
  id: string;
  name: string;
  avatarUrl?: string;
}

export interface Show {
  id: string;
  band_id: string;
  venue_id: string;
  date: string;
  time: string;
  band_name: string;
  band_photo_url?: string;
  band_soundcloud_url?: string;
  venue_name?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  band_members?: BandMember[];
}

// Mock Data
const MOCK_USERS: User[] = [
  { id: 'u1', name: 'Alice Smith', email: 'alice@example.com', role: 'user' },
  { id: 'u2', name: 'Bob Jones', email: 'bob@example.com', role: 'admin' },
];

const MOCK_MUSICIANS: Musician[] = [
  { id: 'm1', first_name: 'Alice', last_name: 'Smith' },
  { id: 'm2', first_name: 'Charlie', last_name: 'Watts' },
  { id: 'm3', first_name: 'Jimi', last_name: 'Hendrix' },
];

const MOCK_BANDS: Band[] = [
  { id: 'b1', name: 'The Rockers', genre: 'Rock', ownerId: 'm1', description: 'Best rock band in town.' },
  { id: 'b2', name: 'Jazz Trio', genre: 'Jazz', ownerId: 'm2', description: 'Smooth jazz for your soul.' },
];

const MOCK_VENUES: Venue[] = [
  { id: 'v1', name: 'The Grand Hall', address: '123 Main St, Cityville', capacity: 500 },
  { id: 'v2', name: 'Dive Bar', address: '456 Back Alley, Townsville', capacity: 50 },
];

const MOCK_SHOWS: Show[] = [
  { id: 's1', band_id: 'b1', venue_id: 'v1', date: '2023-11-20', time: '8:00 PM', band_name: 'The Rockers', venue_name: 'The Grand Hall' },
  { id: 's2', band_id: 'b2', venue_id: 'v2', date: '2023-11-21', time: '9:00 PM', band_name: 'Jazz Trio', venue_name: 'Dive Bar' },
];


// Helper constraint to simulate network delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));


// --- Authentication & User Management ---

export interface AuthResponse {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  token: string;
  refreshToken: string;
  expiresIn: string;
  claims: {
    roles?: string[];
    permissions?: string[];
    bandRoles?: Record<string, { role: string; permissions: string[] }>;
  };
}

// POST /api/auth/refresh
export const refresh_token = async (refreshToken: string): Promise<AuthResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.details || 'Token refresh failed');
  }

  return response.json();
};

// POST /api/auth/refresh-band-permissions
// Refreshes the current user's band role permissions to use the latest templates
export const refresh_band_permissions = async (): Promise<{ message: string; bandRoles: Record<string, any> }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/auth/refresh-band-permissions`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to refresh band permissions');
  }

  return response.json();
};

export interface UpdateMyProfileData {
  displayName?: string;
  photoURL?: string;
}

// PUT /api/users/me - self-service, no admin permission required. Fans have
// no Firestore profile doc at all; displayName/photoURL are Firebase Auth's
// own native fields.
export const update_my_profile = async (data: UpdateMyProfileData): Promise<{ uid: string; displayName: string | null; photoURL: string | null }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/me`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update profile');
  }

  return response.json();
};

// POST /api/users/me/photo
// Deliberately doesn't use authenticatedFetch: it always sets
// Content-Type: application/json, which would stop the browser from setting
// its own multipart/form-data boundary for the FormData body.
export const upload_my_photo = async (file: File): Promise<{ url: string; path: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { getAuthToken } = await import('./auth');
  const token = await getAuthToken();
  if (!token) throw new Error('No authenticated user. Please sign in.');

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${baseUrl}/api/users/me/photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload photo');
  }

  return response.json();
};

export interface UpgradeToMusicianData {
  musicianId?: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  photo_url?: string;
  soundcloud_url?: string;
  genres?: string[];
  instruments?: string[];
}

// POST /api/users/me/upgrade-to-musician - self-service. Claims changed
// server-side; caller must call refreshAuth() afterward to pick up the new
// role/permissions in a fresh token before treating the account as upgraded.
export const upgrade_to_musician = async (data: UpgradeToMusicianData = {}): Promise<{ message: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/me/upgrade-to-musician`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upgrade to musician');
  }

  return response.json();
};

// POST /api/auth/signin
export const sign_in = async (email: string, password: string): Promise<AuthResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/auth/signin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.details || 'Authentication failed');
  }

  return response.json();
};

export interface SignUpData {
  email: string;
  password: string;
  displayName?: string;
  accountType: 'musician' | 'fan';
  musicianId?: string; // Optional: link to existing musician record
  bandId?: string; // Optional: band to claim during signup
  // Musician profile fields (only used when creating new musician profile)
  bio?: string;
  photo_url?: string;
  soundcloud_url?: string;
  genres?: string[];
  instruments?: string[];
}

// POST /api/auth/signup
export const sign_up = async (data: SignUpData): Promise<AuthResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/auth/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || error.details || 'Registration failed');
  }

  return response.json();
};

// POST /register (deprecated - use sign_up instead)
export const register_account = async (data: any) => {
  await delay(500);
  return { message: 'Registered successfully', user: MOCK_USERS[0] };
};

// POST /login (deprecated - use sign_in instead)
export const login = async (credentials: any) => {
  await delay(500);
  return { message: 'Logged in successfully', token: 'mock-token', user: MOCK_USERS[0] };
};

// POST /logout
export const logout = async () => {
  await delay(200);
  return { message: 'Logged out successfully' };
};

// GET /api/users/:id
export const view_profile = async (id: string) => {
  await delay(300);
  return MOCK_USERS.find(u => u.id === id) || null;
};

// Firebase User from Admin SDK
export interface FirebaseUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  disabled: boolean;
  creationTime: string;
  lastSignInTime: string | null;
  claims: {
    roles?: string[];
    permissions?: string[];
    bandRoles?: Record<string, { role: string; permissions: string[] }>;
  };
}

export interface ListUsersResponse {
  users: FirebaseUser[];
  pageToken?: string;
}

// GET /api/users - List all Firebase users (admin only)
export const list_users = async (): Promise<ListUsersResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list users');
  }

  return response.json();
};

// POST /api/users/:uid/reset-password - Send password reset email
export const send_password_reset = async (uid: string): Promise<{ message: string; email: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/reset-password`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send password reset email');
  }

  return response.json();
};

// POST /api/users/:uid/disable - Disable user account
export const disable_user = async (uid: string): Promise<{ message: string; user: { uid: string; disabled: boolean } }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/disable`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to disable user');
  }

  return response.json();
};

// POST /api/users/:uid/enable - Enable user account
export const enable_user = async (uid: string): Promise<{ message: string; user: { uid: string; disabled: boolean } }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/enable`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to enable user');
  }

  return response.json();
};

// GET /api/users/:uid/musician - Get linked musician profile
export const get_linked_musician = async (uid: string): Promise<{ musician: Musician | null }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/musician`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get linked musician');
  }

  return response.json();
};

// DELETE /api/users/:uid/musician - Unlink musician profile from user
export const unlink_musician = async (uid: string): Promise<{ message: string; musicianId: string; musicianName: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/musician`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unlink musician');
  }

  return response.json();
};

// GET /api/users/:uid/bands - Get bands claimed by a user
export const get_linked_bands = async (uid: string): Promise<{ bands: Band[] }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/bands`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get linked bands');
  }

  return response.json();
};

// DELETE /api/users/:uid/bands/:bandId - Unlink band from user
export const unlink_band = async (uid: string, bandId: string): Promise<{ message: string; bandId: string; bandName: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/${uid}/bands/${bandId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unlink band');
  }

  return response.json();
};

// POST /api/users/sync-contributors - Sync show contributors from claims to collection (admin only)
export const sync_contributors = async (): Promise<{
  message: string;
  usersProcessed: number;
  contributorsCreated: number;
  contributorsSkipped: number;
  errors: Array<{ uid: string; bandId: string; error: string }>;
}> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/users/sync-contributors`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to sync contributors');
  }

  return response.json();
};

// GET /api/bands/unclaimed - Get unclaimed bands
export const list_unclaimed_bands = async (): Promise<Band[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/bands/unclaimed`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch unclaimed bands: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// POST /api/bands/:id/claim - Claim a band for the current user (musicians only)
export const claim_band = async (bandId: string): Promise<{
  message: string;
  band: Band;
  musicianId: string;
  bandMemberId: string | null;
  role: string;
}> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/bands/${bandId}/claim`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to claim band');
  }

  return response.json();
};

// PUT /api/users/:id
export const update_profile = async (id: string, data: any) => {
  await delay(300);
  return { ...MOCK_USERS[0], ...data };
};


// --- Musicians ---

// GET /api/musicians/unclaimed - List musicians without linked user accounts
export const list_unclaimed_musicians = async (): Promise<Musician[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/musicians/unclaimed`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch unclaimed musicians: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/musicians
export const list_musicians = async (): Promise<Musician[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch musicians: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/musicians/:id
export const view_musician = async (id: string): Promise<Musician | null> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians/${id}`, {
    method: 'GET',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch musician: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/musicians/me - Get current user's linked musician profile
export const get_my_musician = async (): Promise<Musician | null> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  console.log('[get_my_musician] Fetching musician profile...');
  const response = await authenticatedFetch(`${baseUrl}/api/musicians/me`, {
    method: 'GET',
  });

  console.log('[get_my_musician] Response status:', response.status);

  if (response.status === 404) {
    console.log('[get_my_musician] No musician profile found (404)');
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[get_my_musician] Error response:', errorText);
    throw new Error(`Failed to fetch musician profile: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('[get_my_musician] Musician data:', data);
  return data;
};

export interface UpdateMusicianData {
  first_name?: string;
  last_name?: string;
  bio?: string;
  photo_url?: string;
  has_photo?: boolean;
  soundcloud_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  website_url?: string;
  genres?: string[];
  instruments?: string[];
}

// PUT /api/musicians/:id
export const update_musician = async (id: string, data: UpdateMusicianData): Promise<Musician> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/musicians/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update musician');
  }

  return response.json();
};

// POST /api/musicians/:id/photo
// Deliberately doesn't use authenticatedFetch: it always sets
// Content-Type: application/json, which would stop the browser from setting
// its own multipart/form-data boundary for the FormData body. Attach the
// token manually instead and let fetch set Content-Type itself.
export const upload_musician_photo = async (id: string, file: File): Promise<{ url: string; path: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { getAuthToken } = await import('./auth');
  const token = await getAuthToken();
  if (!token) throw new Error('No authenticated user. Please sign in.');

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${baseUrl}/api/musicians/${id}/photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload photo');
  }

  return response.json();
};

// DELETE /api/musicians/:id
export const delete_musician = async (id: string): Promise<{ message: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/musicians/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete musician');
  }

  return response.json();
};

// GET /api/musicians/:id/bands
export const list_musician_bands = async (musicianId: string): Promise<Band[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians/${musicianId}/bands`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch musician bands: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/musicians/:id/shows
export const list_musician_shows = async (musicianId: string): Promise<Show[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians/${musicianId}/shows`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch musician shows: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/musicians/:id/show-comments
export const list_musician_show_comments = async (musicianId: string): Promise<ShowComment[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians/${musicianId}/show-comments`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch musician show comments: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/musicians/:id/show-media
export const list_musician_show_media = async (musicianId: string): Promise<ShowMedia[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians/${musicianId}/show-media`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch musician show media: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// --- Musician Links ---

// GET /api/musicians/:id/links
export const list_musician_links = async (musicianId: string): Promise<MusicianLink[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/musicians/${musicianId}/links`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch musician links: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface CreateMusicianLinkData {
  title: string;
  url: string;
  order?: number;
}

// POST /api/musicians/:id/links
export const create_musician_link = async (
  musicianId: string,
  data: CreateMusicianLinkData
): Promise<MusicianLink> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/musicians/${musicianId}/links`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create musician link');
  }

  return response.json();
};

export interface UpdateMusicianLinkData {
  title?: string;
  url?: string;
  order?: number;
}

// PUT /api/musicians/:id/links/:linkId
export const update_musician_link = async (
  musicianId: string,
  linkId: string,
  data: UpdateMusicianLinkData
): Promise<MusicianLink> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/musicians/${musicianId}/links/${linkId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update musician link');
  }

  return response.json();
};

// DELETE /api/musicians/:id/links/:linkId
export const delete_musician_link = async (
  musicianId: string,
  linkId: string
): Promise<{ id: string; deleted: boolean }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/musicians/${musicianId}/links/${linkId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete musician link');
  }

  return response.json();
};


// --- Bands ---

// GET /api/bands
export const list_bands = async (): Promise<Band[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch bands: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/bands/:id
export const view_band = async (id: string): Promise<Band | null> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands/${id}`, {
    method: 'GET',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch band: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface CreateBandData {
  name: string;
  genres: string[];
  city: string;
  state: string;
  bio?: string;
  spotify_url?: string;
  instagram_url?: string;
  soundcloud_url?: string;
}

// POST /api/bands
export const create_band = async (data: CreateBandData): Promise<Band> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/bands`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create band');
  }

  return response.json();
};

// PUT /api/bands/:id
export interface UpdateBandData {
  name?: string;
  bio?: string;
  genres?: string[];
  city?: string;
  state?: string;
  soundcloud_url?: string;
  spotify_url?: string;
  instagram_url?: string;
  photo_url?: string;
}

export const update_band = async (id: string, data: UpdateBandData): Promise<Band> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/bands/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update band');
  }

  return response.json();
};

// POST /api/bands/:id/photo
// Same reasoning as upload_musician_photo - deliberately bypasses
// authenticatedFetch's forced Content-Type: application/json so the browser
// can set its own multipart/form-data boundary for the FormData body.
export const upload_band_photo = async (id: string, file: File): Promise<{ url: string; path: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { getAuthToken } = await import('./auth');
  const token = await getAuthToken();
  if (!token) throw new Error('No authenticated user. Please sign in.');

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${baseUrl}/api/bands/${id}/photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload photo');
  }

  return response.json();
};

// DELETE /api/bands/:id
export const delete_band = async (id: string): Promise<{ message: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/bands/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete band');
  }

  return response.json();
};

// GET /api/bands/:id/musicians
export const view_band_musicians = async (bandId: string): Promise<Musician[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands/${bandId}/musicians`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch band musicians: ${response.status} ${response.statusText}`);
  }

  return response.json();
};


// --- Shows ---

// GET /api/bands/:id/shows
export const list_band_shows = async (bandId: string): Promise<Show[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands/${bandId}/shows`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch band shows: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Helper for "All Shows" view in UI
export const list_all_shows = async (): Promise<Show[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/band-shows`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch shows: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Paginated shows response type
export interface PaginatedShowsResponse {
  shows: Show[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasMore: boolean;
  hasPrev: boolean;
}

// Paginated shows params
export interface PaginatedShowsParams {
  cursor?: string;
  direction?: 'future' | 'past' | 'around';
  limit?: number;
}

// GET /api/band-shows/paginated - Paginated shows for infinite scroll
export const list_shows_paginated = async (params: PaginatedShowsParams = {}): Promise<PaginatedShowsResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const searchParams = new URLSearchParams();
  if (params.cursor) searchParams.set('cursor', params.cursor);
  if (params.direction) searchParams.set('direction', params.direction);
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const url = `${baseUrl}/api/band-shows/paginated${searchParams.toString() ? `?${searchParams}` : ''}`;

  const response = await optionalAuthFetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch shows: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// GET /api/bands/:id/shows/:id
export const view_show = async (showId: string): Promise<Show | null> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/band-shows/${showId}`, {
    method: 'GET',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch show: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface CreateShowData {
  band_id: string;
  band_name: string;
  venue_id?: string;
  venue_name?: string;
  address: string;
  latitude?: number;
  longitude?: number;
  date: string;
  time: string;
  description?: string;
}

// POST /api/band-shows
export const create_show = async (data: CreateShowData): Promise<Show> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-shows`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create show');
  }

  return response.json();
};

// DELETE /api/band-shows/:id
export const delete_show = async (showId: string): Promise<{ message: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-shows/${showId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete show');
  }

  return response.json();
};

// PUT /api/band-shows/:id
export const update_show = async (showId: string, data: Partial<CreateShowData>): Promise<Show> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-shows/${showId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update show');
  }

  return response.json();
};

export interface CreateCommentData {
  band_id: string;
  band_name: string;
  show_id: string;
  venue_id?: string;
  venue_name?: string;
  comment: string;
  user_name?: string;
  user_tags?: string[];
}

// POST /api/bands/:bandId/shows/:showId/comments
export const create_show_comment = async (
  bandId: string,
  showId: string,
  data: CreateCommentData
): Promise<ShowComment> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(
    `${baseUrl}/api/bands/${bandId}/shows/${showId}/comments`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create comment');
  }

  return response.json();
};

export interface UploadMediaData {
  band_name: string;
  venue_id?: string;
  venue_name?: string;
  user_name?: string;
  user_tags?: string[];
}

// POST /api/bands/:bandId/shows/:showId/media (multipart/form-data)
export const upload_show_media = async (
  bandId: string,
  showId: string,
  file: File,
  data: UploadMediaData
): Promise<ShowMedia> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { getAuthToken } = await import('./auth');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('band_name', data.band_name);
  if (data.venue_id) formData.append('venue_id', data.venue_id);
  if (data.venue_name) formData.append('venue_name', data.venue_name);
  if (data.user_name) formData.append('user_name', data.user_name);
  if (data.user_tags) formData.append('user_tags', JSON.stringify(data.user_tags));

  const token = await getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${baseUrl}/api/bands/${bandId}/shows/${showId}/media`,
    {
      method: 'POST',
      headers,
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload media');
  }

  return response.json();
};

export interface Genre {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

// GET /api/genres
export const list_genres = async (): Promise<Genre[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/genres`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch genres: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface Instrument {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

// GET /api/instruments
export const list_instruments = async (): Promise<Instrument[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/instruments`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch instruments: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Fan/User public profile
export interface FanProfile {
  uid: string;
  displayName: string;
  email?: string;
  roles: string[];
  isMusician: boolean;
  isFan: boolean;
  contributorBands: { id: string; name: string }[];
  comments: ShowComment[];
  media: ShowMedia[];
}

export const get_fan_profile = async (uid: string): Promise<FanProfile> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/fans/${uid}`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch fan profile: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface BandGenre {
  id: string;
  genre_id: string;
  genre_name: string;
  band_id: string;
  band_name?: string;
  created_at?: string;
  updated_at?: string;
}

// GET /api/bands/:id/genres
export const list_band_genres = async (bandId: string): Promise<BandGenre[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands/${bandId}/genres`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch band genres: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface ShowComment {
  id: string;
  user_id?: string;
  created_by_id?: string;
  user_name?: string;
  user_avatar_url?: string;
  comment: string;
  timestamp?: string;
  created_at?: string;
  user_tags?: string[];
  show_id?: string;
  band_name?: string;
  band_photo_url?: string;
  venue_name?: string;
  show_date?: string;
}

// GET /api/bands/:id/show-comments
export const list_band_show_comments = async (bandId: string): Promise<ShowComment[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands/${bandId}/show-comments`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch band show comments: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface ShowMedia {
  id: string;
  user_id?: string;
  created_by_id?: string;
  url: string;
  thumbnail_url?: string;
  user_name?: string;
  user_avatar_url?: string;
  timestamp?: string;
  created_at?: string;
  user_tags?: string[];
  show_id?: string;
  band_name?: string;
  band_photo_url?: string;
  venue_name?: string;
  show_date?: string;
}

// GET /api/bands/:id/show-media
export const list_band_show_media = async (bandId: string): Promise<ShowMedia[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/bands/${bandId}/show-media`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch band show media: ${response.status} ${response.statusText}`);
  }

  return response.json();
};


// --- Venues ---

// GET /api/venues
export const list_venues = async (): Promise<Venue[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/venues`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch venues: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/venues/:id
export const view_venue = async (id: string): Promise<Venue | null> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/venues/${id}`, {
    method: 'GET',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch venue: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// DELETE /api/venues/:id
export const delete_venue = async (id: string): Promise<{ message: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/venues/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete venue');
  }

  return response.json();
};

// GET /api/venues/:id/shows
export const list_venue_shows = async (venueId: string): Promise<Show[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/venues/${venueId}/shows`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch venue shows: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/venues/:id/show-comments
export const list_venue_show_comments = async (venueId: string): Promise<ShowComment[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/venues/${venueId}/show-comments`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch venue show comments: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/venues/:id/show-media
export const list_venue_show_media = async (venueId: string): Promise<ShowMedia[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/venues/${venueId}/show-media`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch venue show media: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/venues/:id/bands
export const list_venue_bands = async (venueId: string): Promise<Band[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/venues/${venueId}/bands`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch venue bands: ${response.status} ${response.statusText}`);
  }

  return response.json();
};


// --- Band Members ---

export interface BandMemberRecord {
  id: string;
  band_id: string;
  musician_id: string;
  role: string;
  created_at?: string;
  firebaseClaimsUpdated?: boolean;
}

// GET /api/band-members/band/:bandId - Get musicians that are members of a band
export const list_band_members = async (bandId: string): Promise<Musician[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/band-members/band/${bandId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch band members: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// GET /api/band-members/band/:bandId/available - Get musicians NOT in a band
export const list_available_musicians_for_band = async (bandId: string): Promise<Musician[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-members/band/${bandId}/available`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch available musicians');
  }

  return response.json();
};

// POST /api/band-members/band/:bandId/add - Add existing musician to band
export const add_musician_to_band = async (
  bandId: string,
  musicianId: string,
  role: string = 'member'
): Promise<BandMemberRecord> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-members/band/${bandId}/add`, {
    method: 'POST',
    body: JSON.stringify({ musician_id: musicianId, role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add musician to band');
  }

  return response.json();
};

export interface CreateMusicianData {
  first_name: string;
  last_name: string;
  instruments?: string[];
  genres?: string[];
  bio?: string;
  photo_url?: string;
  soundcloud_url?: string;
}

export interface CreateMusicianAndAddResponse {
  musician: Musician;
  bandMember: BandMemberRecord;
}

// POST /api/band-members/band/:bandId/create-and-add - Create new musician and add to band
export const create_musician_and_add_to_band = async (
  bandId: string,
  musician: CreateMusicianData,
  role: string = 'member'
): Promise<CreateMusicianAndAddResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-members/band/${bandId}/create-and-add`, {
    method: 'POST',
    body: JSON.stringify({ musician, role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create musician and add to band');
  }

  return response.json();
};

// DELETE /api/band-members/band/:bandId/musician/:musicianId - Remove musician from band
export const remove_musician_from_band = async (
  bandId: string,
  musicianId: string
): Promise<{ id: string; removed: boolean }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(
    `${baseUrl}/api/band-members/band/${bandId}/musician/${musicianId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove musician from band');
  }

  return response.json();
};

// PUT /api/band-members/band/:bandId/musician/:musicianId/role - Update band member role
export const update_band_member_role = async (
  bandId: string,
  musicianId: string,
  role: 'owner' | 'admin' | 'member'
): Promise<{ id: string; band_id: string; musician_id: string; role: string; previousRole: string }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(
    `${baseUrl}/api/band-members/band/${bandId}/musician/${musicianId}/role`,
    {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update band member role');
  }

  return response.json();
};


// --- Show Contributors ---

// Contributor record from band_show_contributors table
export interface ShowContributorRecord {
  id: string;
  band_id: string;
  band_name: string;
  user_id: string;
  user_email: string;
  user_name: string;
  created_at?: string;
  firebaseClaimsUpdated?: boolean;
}

// Available fan (from Firebase Auth)
export interface AvailableFan {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
}

// For backwards compatibility
export interface ShowContributor {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role?: string;
}

// GET /api/band-show-contributors/band/:bandId - Get show contributors for a band
export const list_show_contributors = async (bandId: string): Promise<ShowContributorRecord[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { optionalAuthFetch } = await import('./auth');

  const response = await optionalAuthFetch(`${baseUrl}/api/band-show-contributors/band/${bandId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch show contributors');
  }

  return response.json();
};

// GET /api/band-show-contributors/user/:userId - Get every band a user contributes to.
// Empty array means "not a contributor for any band". Route sits under the
// protected router (authorize('band_members', 'r')), so this needs a logged-in
// caller - it's not usable for an anonymous/logged-out viewer.
export const list_contributor_bands = async (userId: string): Promise<Band[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-show-contributors/user/${userId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch contributor bands');
  }

  return response.json();
};

// GET /api/band-show-contributors/band/:bandId/available - Get fans available to be contributors
export const list_available_contributors = async (bandId: string): Promise<AvailableFan[]> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-show-contributors/band/${bandId}/available`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch available contributors');
  }

  return response.json();
};

// POST /api/band-show-contributors/band/:bandId/add - Add a user as show contributor
export const add_show_contributor = async (
  bandId: string,
  userId: string
): Promise<ShowContributorRecord> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-show-contributors/band/${bandId}/add`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add contributor');
  }

  return response.json();
};

// DELETE /api/band-show-contributors/band/:bandId/user/:userId - Remove a show contributor
export const remove_show_contributor = async (
  bandId: string,
  userId: string
): Promise<{ id: string; removed: boolean }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-show-contributors/band/${bandId}/user/${userId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove contributor');
  }

  return response.json();
};

// DELETE /api/band-show-contributors/band/:bandId/me - Remove the authenticated user as a show contributor
export const remove_my_show_contributor = async (
  bandId: string
): Promise<{ id: string; removed: boolean }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const { authenticatedFetch } = await import('./auth');

  const response = await authenticatedFetch(`${baseUrl}/api/band-show-contributors/band/${bandId}/me`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove contributor');
  }

  return response.json();
};

/**
 * Upload a profile photo via the API server
 * @param file - The image file to upload
 * @returns The download URL of the uploaded image
 */
export const upload_profile_photo = async (file: File): Promise<string> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${baseUrl}/api/musicians/upload-photo`, {
    method: 'POST',
    headers: { 'ngrok-skip-browser-warning': 'true' },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload photo');
  }

  const result = await response.json();
  return result.url;
};


// --- Network Graph ---

export interface NetworkNode {
  id: string;
  type: 'musician' | 'band';
  name: string;
  photo_url: string | null;
  instruments?: string[];
  connectionCount: number;
}

export interface NetworkLink {
  source: string;
  target: string;
}

export interface NetworkStats {
  musicianCount: number;
  bandCount: number;
  connectionCount: number;
}

export interface TopMusician {
  id: string;
  name: string;
  photo_url: string | null;
  connectionCount: number;
}

export interface NetworkGraphResponse {
  nodes: NetworkNode[];
  links: NetworkLink[];
  stats: NetworkStats;
  topMusicians: TopMusician[];
}

// GET /api/network/graph - Get the full network graph
export const get_network_graph = async (): Promise<NetworkGraphResponse> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/network/graph`, {
    method: 'GET',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch network graph: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Feedback
export interface FeedbackSubmission {
  userId: string;
  userEmail: string;
  feedback: string;
}

export const submitFeedback = async (data: FeedbackSubmission): Promise<{ success: boolean }> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/api/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText}`);
  }

  return response.json();
};
